<!-- Part of the SPARKL educational activity system, Copyright 2019 by Pepper Williams -->
<template>
	<k-dialog v-model="dialog_open" persistent max-width="800px">
		<k-card>
			<k-card-title><b>Edit User Admin Rights</b></k-card-title>
			
			<k-card-text>
				<div style="font-size:1.2em"><b>{{admin_user.last_name}}, {{admin_user.first_name}} ({{admin_user.email}})</b></div>

				<k-checkbox :label="'“Superuser” admin rights'" class="checkbox" v-model="su" @change="right_changed('su')" hide-details></k-checkbox>
				<k-checkbox :label="'Mimic Any User'" class="checkbox" :disabled="su" v-model="mimic_all" @change="right_changed('mimic_all')" hide-details></k-checkbox>
				<k-checkbox :label="'View Resource Usage Report'" class="checkbox" :disabled="su" v-model="res_rep" @change="right_changed('res_rep')" hide-details></k-checkbox>
				
				<div class="mt-4">
					<b>Courses that can be EDITED:</b>
					<div class="mt-1">
						<k-chip :disabled="su" v-for="lp in lps" :key="lp.code" small class="mr-1" close @click:close="remove_right(lp.code)">{{lp.text}}</k-chip>
						<k-btn v-show="!su && !show_add_lp_interface" small color="primary" @click="show_add_lp_interface=true">
							<k-icon class="mr-1" small>fas fa-plus</k-icon> Add
						</k-btn>
						<i class="grey--text text--darken-1" v-if="su">Note: Superusers can edit all courses.</i>
					</div>
					<div class="mt-2" style="display:flex" v-show="show_add_lp_interface">
						<div style="width:400px"><k-autocomplete outlined hide-details clearable v-model="lp_add_item" :items="lp_add_menu_items" label="Choose Course EDIT Rights" solo dense></k-autocomplete></div>
						<k-btn class="ml-2 mt-1" color="primary" @click="add_course_right('lp')" :disabled="!lp_add_item"><k-icon class="mr-1" small>fas fa-plus</k-icon> Add</k-btn>
					</div>
				</div>

				<div class="mt-4">
					<b>Courses that can be VIEWED (even when tagged as inactive):</b>
					<div class="mt-1">
						<k-chip :disabled="su" v-for="view_lp in view_lps" :key="view_lp.code" small class="mr-1" close @click:close="remove_right(view_lp.code)">{{view_lp.text}}</k-chip>
						<k-btn v-show="!su && !show_add_view_lp_interface" small color="primary" @click="show_add_view_lp_interface=true">
							<k-icon class="mr-1" small>fas fa-plus</k-icon> Add
						</k-btn>
						<i class="grey--text text--darken-1" v-if="su">Note: Superusers can view all courses.</i>
					</div>
					<div class="mt-2" style="display:flex" v-show="show_add_view_lp_interface">
						<div style="width:400px">
							<k-autocomplete outlined hide-details clearable v-model="view_lp_add_item" :items="lp_add_menu_items" label="Choose Course VIEW Rights" solo dense/>
						</div>
						<k-btn class="ml-2 mt-1" color="primary" :disabled="!view_lp_add_item" @click="add_course_right('view_lp')">
							<k-icon class="mr-1" small>fas fa-plus</k-icon> Add
						</k-btn>
					</div>
				</div>

				<div class="mt-4" v-if="site_config.show_pd=='yes'">
					<b>PD Report Access:</b>
					<div class="mt-1">
						<k-chip :disabled="su" v-for="pd_rep in pd_reps" :key="pd_rep.code" small class="mr-1" close @click:close="remove_right(pd_rep.code)">{{pd_rep.text}}</k-chip>
						<k-btn v-show="!su&&!show_add_pd_rep_interface" small color="primary" @click="show_add_pd_rep_interface = true">
							<k-icon class="mr-1" small>fas fa-plus</k-icon> Add
						</k-btn>
					</div>
					<div class="mt-2" style="display:flex" v-show="show_add_pd_rep_interface">
						<div style="width:400px">
							<k-autocomplete outlined hide-details clearable v-model="pd_rep_add_item" :items="pd_rep_add_menu_items" label="Choose Division or School" solo dense/>
						</div>
						<k-btn class="ml-2 mt-1" color="primary" :disabled="!pd_rep_add_item" @click="add_pd_rep">
							<k-icon class="mr-1" small>fas fa-plus</k-icon> Add
						</k-btn>
					</div>
				</div>

				<div v-if="site_config.asset_manager.enabled" class="mt-4">
					<b>{{site_config.asset_manager.text.name_simple}} Publishing Roles:</b>
					<k-checkbox v-model="asset_manager_reviewer" :label="'Reviewer'" class="checkbox mr-5" :disabled="su" hide-details @change="asset_right_changed('reviewer')"/>
					
					<div v-if="asset_manager_reviewer" class="d-flex mt-2">
						<k-chip :disabled="su" v-for="asset in assets.reviewer" :key="asset.code" small class="mr-1" close @click:close="remove_right(asset.code)">
							{{asset.text}}
						</k-chip>
					</div>

					<div v-if="asset_manager_reviewer" class="d-flex my-2">
						<div v-if="add_asset_reviewer.show" class="autocomplete mr-2">
							<k-autocomplete
								v-model="add_asset_reviewer.item"
								:items="asset_add_menu_items"
								label="Choose Asset Reviewer Rights"
								outlined hide-details clearable solo dense
							/>
						</div>

						<k-btn color="primary" class="mt-1" :disabled="add_asset_reviewer.show && !add_asset_reviewer.item" @click="add_asset_right('reviewer')">
							<k-icon class="mr-1" small>fas fa-plus</k-icon> Add
						</k-btn>
					</div>

					<k-checkbox v-model="asset_manager_publisher" :label="'Publisher'" class="checkbox" :disabled="su" hide-details @change="asset_right_changed('publisher')"/>

					<div v-if="asset_manager_publisher" class="d-flex mt-2">
						<k-chip :disabled="su" v-for="asset in assets.publisher" :key="asset.code" small class="mr-1" close @click:close="remove_right(asset.code)">
							{{asset.text}}
						</k-chip>
					</div>

					<div v-if="asset_manager_publisher" class="d-flex mt-2">
						<div v-if="add_asset_publisher.show" class="autocomplete mr-2">
							<k-autocomplete
								v-model="add_asset_publisher.item"
								:items="asset_add_menu_items"
								label="Choose Asset Publisher Rights"
								outlined hide-details clearable solo dense
							/>
						</div>

						<k-btn color="primary" class="mt-1" :disabled="add_asset_publisher.show && !add_asset_publisher.item" @click="add_asset_right('publisher')">
							<k-icon class="mr-1" small>fas fa-plus</k-icon> Add
						</k-btn>
					</div>
				</div>
			</k-card-text>

			<k-card-actions class="pl-4 pr-4 pb-4">
				<k-btn color="red" dark @click="remove_all_rights"><k-icon small class="mr-2">fas fa-trash</k-icon> Remove All Admin Rights for User</k-btn>
				<k-spacer/>
				<k-btn color="secondary" @click="$emit('editor_cancel')" class="mr-1"><k-icon small class="mr-2">fas fa-times</k-icon> Cancel</k-btn>
				<k-btn color="primary" @click="save_edits"><k-icon small class="mr-2">fas fa-check</k-icon> Save</k-btn>
			</k-card-actions>
		</k-card>
	</k-dialog>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

export default {
	props: {
		admin_user: { type: Object, required: true },
	},

	data() {
		return {
			dialog_open: true,
			rights: [],
			su: false,
			pd: false,
			pg: false,
			mimic_all: false,
			res_rep: false, // resource report
			asset_manager_reviewer: false,
			asset_manager_publisher: false,
			lps: [],
			view_lps: [],
			pd_reps: [],
			assets: {reviewer: [], publisher: []},
			lp_add_item: null,
			pd_rep_add_item: null,
			view_lp_add_item: null,
			asset_add_item: null,
			show_add_lp_interface: false,
			show_add_view_lp_interface: false,
			show_add_pd_rep_interface: false,
			add_asset_reviewer: {
				show: false,
				item: undefined
			},
			add_asset_publisher: {
				show: false,
				item: undefined
			}
		}
	},

	computed: {
		...mapState(['user_info', 'all_courses', 'site_config', 'todo_user_group_divisions', 'todo_user_group_schools',]),

		lp_add_menu_items() {
			let arr = [
				{value:'level.all', text: 'ALL COURSES'},
				{value:'level.Elementary', text: 'ALL Elementary Courses'},
				{value:'level.Middle School', text: 'ALL Middle School Courses'},
				{value:'level.High School', text: 'ALL High School Courses'},
			]
			for (let subject in this.$store.state.subjects) {
				arr.push({ value: 'subject.' + subject, text: 'ALL ' + subject})
			}
			for (let course of this.all_courses) {
				arr.push({ value: 'course.' + course.course_code, text: course.title })
			}

			// note: we will add 'lp.' or 'view_lp.' to the start of the value in add_course_rights
			return arr
		},

		pd_rep_add_menu_items() {
			let arr = [
				{value:'pd_rep.all', text: 'ALL SCHOOLS/DIVISIONS'},
				{header: '--- DIVISIONS ---'},
			]
			for (let uuid in this.todo_user_group_divisions) {
				arr.push({ value: 'pd_rep.' + uuid, text: this.todo_user_group_divisions[uuid] })
			}
			arr.push({header: '--- SCHOOLS ---'})
			for (let uuid in this.todo_user_group_schools) {
				arr.push({ value: 'pd_rep.' + uuid, text: this.todo_user_group_schools[uuid] })
			}
			return arr
		},

		asset_add_menu_items() {
			let arr = [
				{value: 'all.all', text: 'ALL'}
			]

			let subjects = []
			for (let subject in this.$store.state.subjects) {
				arr.push({ value: 'subject.' + subject, text: 'ALL ' + subject})
				subjects.push(subject)
			}

			/** Grades are in state.site_config.grades (account for undefined or non-array though) */
			let grades = this.$store.state.site_config.grades

			if (grades && grades.length) {
				let gradeSubject = []

				grades.forEach(grade => {
					arr.push({value: 'level.' + grade, text: 'ALL GRADE ' + grade})
					
					if (subjects.length) {
						subjects.forEach(subject => {
							gradeSubject.push({value: 'level_subject.' + grade + '_' + subject, text: 'GRADE ' + grade + ' ' + subject})
						})
					}
				})

				if (gradeSubject.length) {
					arr.push(...gradeSubject)
				}
			}

			return arr
		}
	},

	created() {
		this.rights = this.admin_user.rights.concat([])
		this.rights_changed()
	},

	mounted() {
		// DEBUG
		vapp.admin_user_edit = this
	},
	
	methods: {
		rights_changed() {
			// NOTE THAT CODE BELOW IS LARGELY MIRRORED IN AdminUsers.vue
			// if user has su rights, they can do everything else too
			if (this.rights.findIndex(s=>s=='su') > -1) {
				this.su = true
				this.pd = true
				this.pg = true
				this.mimic_all = true
				this.res_rep = true
				this.asset_manager_reviewer = true
				this.asset_manager_publisher = true
				this.lps = [
					{ code: 'lp.level.all', text: 'ALL COURSES' },
				]
				this.view_lps = [
					{ code: 'view_lp.level.all', text: 'ALL COURSES' },
				]
				this.pd_reps = [
					{ code: 'pd_rep.all', text: 'ALL SCHOOLS/DIVISIONS', text_lc: 'all schools/divisions' },
				]

			} else {
				// else build up list from rights
				this.su = false
				this.pd = false
				this.pg = false
				this.mimic_all = false
				this.res_rep = false
				this.asset_manager_reviewer = false
				this.asset_manager_publisher = false
				this.lps = []
				this.view_lps = []
				this.pd_reps = []
				this.assets = {
					reviewer: [],
					publisher: []
				}

				for (var code of this.rights) {
					let lp = /^lp\.(.*?)\.(.*)/.exec(code)
					let view_lp = /^view_lp\.(.*?)\.(.*)/.exec(code)
					let pd_rep = /^pd_rep\.(.*)/.exec(code)
					let asset = /^asset_([a-z]+)\.(.*)\.(.*)/.exec(code)

					if (lp) {
						// Edit courses
						let text = this.get_text(lp[1], lp[2])
						this.lps.push({ code, text })
					} else if (view_lp) {
						// View courses
						let text = this.get_text(view_lp[1], view_lp[2])
						this.view_lps.push({ code, text })
					} else if (pd_rep) {
						// PD reports
						let uuid = pd_rep[1]
						let text
						if (uuid == 'all') {
							text = 'ALL SCHOOLS/DIVISIONS'
						} else {
							if (this.todo_user_group_divisions[uuid]) {
								text = this.todo_user_group_divisions[uuid]
							} else if (this.todo_user_group_schools[uuid]) {
								text = this.todo_user_group_schools[uuid]
							} else {
								text = uuid	// hopefully this won't happen
							}
						}
						this.pd_reps.push({ code, text, text_lc: text.toLowerCase() })
					} else if (asset) {
						// Asset Manager
						let role = asset[1]
						this['asset_manager_' + role] = true
						let l1 = asset[2]
						let l2 = asset[3]
						let text = this.get_text(l1, l2)
						this.assets[role].push({code, text})
					} else {
						this[code] = true
					}
				}
			}
		},

		right_changed(right) {
			if (this[right]) {
				this.add_right(right)
			} else {
				this.remove_right(right)
			}
		},

		asset_right_changed(right) {
			if (!this['asset_manager_' + right]){
				this.rights = this.rights.filter(c => !c.includes('asset_' + right))
				this.rights_changed()
			}
		},

		add_course_right(type) {
			// this[type+'_add_item'] means lp_add_item or view_lp_add_item
			// it will be one of the values of lp_add_menu_items, e.g. 'level.Elementary'
			// we add 'type.' to the front of this string for the right we're adding
			let lai = type + '.' + this[type + '_add_item']
			if (empty(lai)) {
				this.$alert('Please specify a grade level, subject, or course in the menu at left.')
				return
			}
			this.add_right(lai)
			this[type + '_add_item'] = null
		},

		add_pd_rep() {
			if (empty(this.pd_rep_add_item)) {
				this.$alert('Please specify a school or division in the menu at left.')
				return
			}
			this.add_right(this.pd_rep_add_item)
			this.pd_rep_add_item = null
		},

		add_asset_right(type) {
			let asset_type = this['add_asset_' + type]
			if (!asset_type.show) {
				asset_type.show = true
			} else {
				let code = 'asset_' + type + '.' + asset_type.item

				if (this.add_right(code)) {
					asset_type.show = false
					asset_type.item = undefined
				}
			}
		},

		add_right(code) {
			if (!this.rights.find(c=>c==code)) {
				this.rights.push(code)
				this.rights_changed()
				return true
			}
			return false
		},

		remove_right(code) {
			let i = this.rights.findIndex(c => c == code)
			if (i > -1) {
				this.rights.splice(i, 1)
				this.rights_changed()
			}
		},

		remove_all_rights() {
			this.$confirm({
			    title: 'Are you sure?',
			    text: 'Are you sure you want to remove all admin rights for this user?',
			    acceptText: 'Remove Rights',
				acceptColor: 'red',
				dialogMaxWidth: 600
			}).then(y => {
				// just emit rights_edited with an empty array
				this.$emit('rights_edited', [])
			}).catch(n => {console.error(n)})
		},

		save_edits() {
			let rights = []
			if (this.su) {
				rights.push('su')
			} else {
				if (this.pd) rights.push('pd')
				if (this.pg) rights.push('pg')
				if (this.mimic_all) rights.push('mimic_all')
				if (this.res_rep) rights.push('res_rep')
				for (let lp of this.lps) rights.push(lp.code)
				for (let view_lp of this.view_lps) rights.push(view_lp.code)
				for (let pd_rep of this.pd_reps) rights.push(pd_rep.code)
				for (let asset_r of this.assets.reviewer) rights.push(asset_r.code)
				for (let asset_p of this.assets.publisher) rights.push(asset_p.code)
			}

			this.$emit('rights_edited', rights)
		},

		get_text(l1, l2) {
			let text

			if (l2 == 'all') {
				text = 'ALL COURSES'
			} else {
				switch(l1) {
					case 'level':
					case 'subject':
						text = 'ALL ' + l2
						break;
					case 'resource_id':
					case 'lesson_id':
						let prefix = l1.split('_')
						text = prefix[0][0].toUpperCase() + prefix[0].slice(1) + ' ' + prefix[1].toUpperCase() + ': ' + l2
						break;
					case 'level_subject':
						let suffix = l2.split('_')
						text = 'GRADE ' + suffix[0] + ' ' + suffix[1]
						break;
					default:
						// lp.course.1355
						let course = this.all_courses.find(x => x.course_code == l2)
						if (!empty(course)) {
							text = course.title
						} else {
							text = 'Course ID ' + l2
						}
				}
			}

			return text
		},
	}
}
</script>

<style lang="scss" scoped>
.checkbox {
	margin-top: 3px;
}

.autocomplete {
	width: 400px;
}
</style>
